import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Flame, AlertTriangle } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { FlavorCup } from "@/components/site/FlavorCup";
import { OrderButton } from "@/components/site/OrderContext";
import { CATEGORIES, DRINKS, price } from "@/content/menu";
import { LINKS, SITE_URL } from "@/content/site";
import { EASE } from "@/components/site/motion";

const menuLd = {
  "@context": "https://schema.org",
  "@type": "Menu",
  name: "TeaDM Menu",
  url: `${SITE_URL}/menu`,
  hasMenuSection: CATEGORIES.map((c) => ({
    "@type": "MenuSection",
    name: c.name,
    hasMenuItem: DRINKS.filter((d) => d.category === c.slug).map((d) => ({
      "@type": "MenuItem",
      name: d.name,
      ...(d.desc ? { description: d.desc } : {}),
      offers: { "@type": "Offer", price: d.price.toFixed(2), priceCurrency: "USD" },
    })),
  })),
};

const DrinkCard = ({ d, cat }) => (
  <motion.article layout data-testid={`menu-item-${d.id}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.5, ease: EASE }} className="group flex flex-col overflow-hidden rounded-[2rem] bg-white shadow-[0_1px_0_rgba(26,10,16,0.06)] transition-shadow duration-500 hover:shadow-[0_30px_60px_-30px_rgba(26,10,16,0.45)]">
    <div className="relative aspect-[4/3] overflow-hidden" style={{ background: d.image ? cat.color : cat.tint }}>
      {d.image ? (
        <img src={d.image} alt={`${d.name} from TeaDM`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
        <div className="flex h-full items-center justify-center"><FlavorCup color={d.color} label={d.name} className="h-[82%] w-auto transition-transform duration-700 group-hover:-translate-y-2 group-hover:rotate-3" /></div>
      )}
      <span className="absolute right-4 top-4 rounded-full bg-[#1a0a10] px-3.5 py-1.5 font-display text-sm font-extrabold text-[#fff5e8]" data-testid={`menu-price-${d.id}`}>{price(d.price)}</span>
    </div>
    <div className="flex flex-1 flex-col p-6">
      <h3 className="font-display text-2xl font-extrabold leading-tight tracking-tight">{d.name}</h3>
      {d.aka && <p className="font-serif-x mt-1 text-[#1a0a10]/70">aka ‘{d.aka}’</p>}
      {d.desc && <p className="mt-3 text-[15px] text-[#1a0a10]/80">{d.desc}</p>}
      {d.specs.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-sm text-[#1a0a10]/85">
          {d.specs.map((s) => <li key={s} className="flex gap-2"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: cat.color }} />{s}</li>)}
        </ul>
      )}
      {d.recommended && <p className="mt-3 text-sm font-semibold">Recommended with: {d.recommended}</p>}
      <div className="mt-auto flex flex-wrap gap-2 pt-5">
        {d.allergens.length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ffd23f]/50 px-3 py-1 text-xs font-bold"><AlertTriangle className="h-3.5 w-3.5" />Contains {d.allergens.join(" & ").toLowerCase()}</span>}
        {d.notes.map((n) => <span key={n} className="inline-flex items-center gap-1.5 rounded-full bg-[#1a0a10]/5 px-3 py-1 text-xs font-semibold"><Flame className="h-3.5 w-3.5" />{n.replace(/^\*/, "")}</span>)}
      </div>
    </div>
  </motion.article>
);

export default function Menu() {
  const { hash } = useLocation();
  const [cat, setCat] = useState(() => (CATEGORIES.some((c) => `#${c.slug}` === hash) ? hash.slice(1) : "all"));
  const [q, setQ] = useState("");

  useEffect(() => {
    if (CATEGORIES.some((c) => `#${c.slug}` === hash)) setCat(hash.slice(1));
  }, [hash]);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    return CATEGORIES.filter((c) => cat === "all" || c.slug === cat)
      .map((c) => ({ c, items: DRINKS.filter((d) => d.category === c.slug && (!term || `${d.name} ${d.desc} ${d.specs.join(" ")}`.toLowerCase().includes(term))) }))
      .filter((g) => g.items.length);
  }, [cat, q]);

  return (
    <>
      <Seo title="TeaDM Menu — Boba Milk Tea, Vietnamese Coffee & Smoothies" description="Browse the full TeaDM menu: Vietnamese seasalt coffee, milk teas, matcha, fruit teas, smoothies and snacks with prices. Order online from your nearest store." path="/menu" jsonLd={menuLd} />
      <PageHero testId="menu-hero" bg="#ff7a00" fg="#1a0a10" eyebrow="The menu" lines={["Sip the", "whole lineup."]} intro="Vietnamese coffee, seasalt teas, milk teas, signature fruit blends and real-fruit snows — with prices, recipes and allergy notes.">
        <div className="mt-8 flex flex-wrap gap-3">
          <OrderButton className="btn btn-ink" testId="menu-hero-order-btn" />
          <a href={LINKS.popularMenu} target="_blank" rel="noopener noreferrer" data-testid="menu-popular-pdf" className="btn btn-ghost">Popular drinks (PDF)</a>
        </div>
      </PageHero>

      <div className="sticky top-[76px] z-40 border-b border-[#1a0a10]/10 bg-[#fff5e8]/90 backdrop-blur-xl">
        <div className="wrap flex flex-col gap-3 py-3 lg:flex-row lg:items-center">
          <div className="no-scrollbar -mx-5 flex flex-1 gap-2 overflow-x-auto px-5 lg:mx-0 lg:px-0" role="tablist" aria-label="Menu categories">
            {[{ slug: "all", name: "All", color: "#1a0a10" }, ...CATEGORIES].map((c) => (
              <button key={c.slug} type="button" role="tab" aria-selected={cat === c.slug} onClick={() => setCat(c.slug)} data-testid={`menu-filter-${c.slug}`} className={`shrink-0 rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-wide transition-colors duration-300 ${cat === c.slug ? "text-white" : "bg-[#1a0a10]/5 text-[#1a0a10] hover:bg-[#1a0a10]/10"}`} style={cat === c.slug ? { background: c.color } : undefined}>
                {c.name}
              </button>
            ))}
          </div>
          <label className="relative block lg:w-72">
            <span className="sr-only">Search drinks</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1a0a10]/60" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search matcha, mango…" data-testid="menu-search-input" className="w-full rounded-full border-2 border-[#1a0a10]/10 bg-white py-2.5 pl-10 pr-4 text-base focus:border-[#ff2e63] focus:outline-none" />
          </label>
        </div>
      </div>

      <div className="bg-[#fff5e8] pb-24">
        {groups.length === 0 && <p data-testid="menu-empty" className="wrap py-24 text-center text-lg">No drinks match “{q}”. Try another flavor.</p>}
        {groups.map(({ c, items }) => (
          <section key={c.slug} id={c.slug} data-testid={`menu-section-${c.slug}`} className="scroll-mt-40 pt-16">
            <div className="wrap">
              <div className="mb-8 flex flex-col justify-between gap-4 border-b-4 pb-5 sm:flex-row sm:items-end" style={{ borderColor: c.color }}>
                <div>
                  <h2 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight sm:text-6xl" style={{ color: c.color }}>{c.name}</h2>
                  <p className="mt-3 max-w-xl text-[#1a0a10]/80">{c.blurb}</p>
                </div>
                <OrderButton className="btn btn-ink shrink-0 self-start sm:self-auto" testId={`menu-order-${c.slug}`}>Order {c.name}</OrderButton>
              </div>
              <motion.div layout className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence>{items.map((d) => <DrinkCard key={d.id} d={d} cat={c} />)}</AnimatePresence>
              </motion.div>
            </div>
          </section>
        ))}
        <p className="wrap mt-16 text-sm text-[#1a0a10]/70">Prices and availability may vary by location — your store's Toast page shows the live menu.</p>
      </div>
      <div className="fixed inset-x-3 bottom-3 z-40 sm:hidden"><OrderButton className="btn btn-berry w-full py-4 shadow-2xl" testId="menu-mobile-sticky-order-btn" /></div>
    </>
  );
}
